import { requestPath } from './httpFailureReporting'

/**
 * Decides whether BayaWatch session replay may run for a page load.
 *
 * ## Why
 *
 * The SDK (`@bayawatch/sdk` 0.2.0) stamps the raw `window.location.href` on
 * every session-replay request — the `init` payload and each `events` batch —
 * and those requests never pass through `beforeSend`. rrweb adds it again in
 * its Meta event (`data.href`), and its DOM snapshot records `<a href>`
 * attributes such as the issue links on the public report page. The page
 * routes `/reset_password/:token`, `/public/issues/:token`,
 * `/public/reports/:token` and `/public/reports/:token/issues/:issueId` carry a
 * bearer secret in the path, so recording them ships the secret to
 * watch.bayalab.com on a plain visit, with no error involved.
 *
 * ## Rule
 *
 * Deny by default, using the same path redaction as error payloads: replay
 * runs only when every path segment survives `requestPath` unchanged, i.e. is
 * a lowercase resource name or a numeric id. That covers every static and
 * numeric-id route in the app today and refuses any future route that puts an
 * opaque value in the path, without a list to keep in sync with `routes.tsx`.
 *
 * ## Scope
 *
 * The decision is made once, when the SDK is initialized: the SDK cannot start
 * recording later in the same page load, and stopping it flushes one last
 * batch stamped with the then-current URL. A token route is therefore safe
 * when it is the page that was loaded — which is how every one of them is
 * reached (e-mail and share links; the public pages only link to each other).
 * An in-app navigation from a recorded page INTO a token route would still
 * leak; no such link exists today.
 *
 * The query string and hash are not inspected. No route carries a secret
 * there today, and the SDK would send them for any page reached in-app anyway.
 */
export function isReplaySafePage(href: string): boolean {
  try {
    const { pathname } = new URL(href)
    return requestPath(pathname) === pathname
  } catch {
    return false
  }
}
